import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Upload, FileText, Download, Share2, Trash2, X, Tag, Users } from 'lucide-react';

const API_URL = window.location.origin || '';

const Documents = () => {
  const { currentTeam } = useTeam();
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTags, setSearchTags] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const categories = [
    { value: 'all', label: 'All Documents' },
    { value: 'medical', label: 'Medical' },
    { value: 'financial', label: 'Financial' },
    { value: 'legal', label: 'Legal' },
    { value: 'household', label: 'Household' },
    { value: 'caregiving', label: 'Caregiving' },
    { value: 'end_of_life', label: 'End of Life' },
    { value: 'memorial', label: 'Memorial' },
    { value: 'gratitude', label: 'Gratitude' },
  ];

  useEffect(() => {
    if (currentTeam?.id) {
      fetchDocuments();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  useEffect(() => {
    filterDocuments();
  }, [documents, selectedCategory, searchTags]);

  const fetchDocuments = async () => {
    try {
      // Only show loading on initial load
      if (documents.length === 0) {
        setLoading(true);
      }
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/documents`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Error loading documents');
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.dashboard_area === selectedCategory);
    }

    if (searchTags) {
      const tagArray = searchTags.toLowerCase().split(',').map(t => t.trim());
      filtered = filtered.filter(doc =>
        doc.tags.some(tag => tagArray.some(searchTag => tag.toLowerCase().includes(searchTag)))
      );
    }

    setFilteredDocs(filtered);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/documents/${doc.id}/download`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        toast.error('Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error downloading document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/documents/${docId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Document deleted');
        fetchDocuments();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error deleting document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!currentTeam) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Please select a team to view documents</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold">Documents</h1>
              <p className="text-gray-600 mt-1">Manage and share important documents</p>
            </div>
            <div className="flex gap-3">
              <UploadDocumentDialog
                open={uploadDialogOpen}
                onOpenChange={setUploadDialogOpen}
                onSuccess={fetchDocuments}
              />
              <QuickActions position="top" />
            </div>
          </div>

        {/* Filters */}
        <div className="dashboard-card mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="category-filter">Category</Label>
              <select
                id="category-filter"
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <Label htmlFor="tag-filter">Filter by Tags</Label>
              <div className="relative">
                <Input
                  id="tag-filter"
                  placeholder="Search tags (comma separated)"
                  value={searchTags}
                  onChange={(e) => setSearchTags(e.target.value)}
                  className="mt-1"
                />
                {searchTags && (
                  <button
                    onClick={() => setSearchTags('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Document Grid */}
        {filteredDocs.length === 0 ? (
          <div className="text-center py-12 dashboard-card">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No documents found</h3>
            <p className="text-gray-600 mb-4">
              {documents.length === 0
                ? 'Upload your first document to get started'
                : 'Try adjusting your filters'}
            </p>
            {documents.length === 0 && (
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocs.map(doc => (
              <DocumentCard
                key={doc.id}
                document={doc}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onShare={() => fetchDocuments()}
              />
            ))}
          </div>
        )}
        
        {/* Quick Actions - Bottom */}
        <div className="mt-8">
          <QuickActions position="bottom" />
        </div>
      </div>
    </div>
    </Layout>
  );
};

const DocumentCard = ({ document, onDownload, onDelete, onShare }) => {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const getFileIcon = () => {
    return <FileText className="w-8 h-8 text-blue-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {getFileIcon()}
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg truncate">{document.title}</CardTitle>
              <CardDescription className="text-sm">{document.file_name}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {document.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{document.description}</p>
        )}

        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Size:</span>
            <span className="font-medium">{formatFileSize(document.file_size)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Uploaded:</span>
            <span className="font-medium">{formatDate(document.uploaded_at)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Category:</span>
            <Badge variant="outline">{document.dashboard_area}</Badge>
          </div>
        </div>

        {document.tags && document.tags.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {document.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {(document.shared_with_all_support || document.shared_with_all_providers || document.shared_with_member_ids?.length > 0) && (
          <div className="mb-4 pb-4 border-b">
            <div className="flex items-center gap-1 text-xs text-gray-500 flex-wrap">
              <Users className="w-3 h-3" />
              <span>Shared:</span>
              {document.shared_with_all_support && <Badge variant="secondary" className="text-xs">Support</Badge>}
              {document.shared_with_all_providers && <Badge variant="secondary" className="text-xs">Providers</Badge>}
              {document.shared_with_member_ids?.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {document.shared_with_member_ids.length} member(s)
                </Badge>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => onDownload(document)}
          >
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>
          <ShareDocumentDialog
            document={document}
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            onSuccess={onShare}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(document.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const UploadDocumentDialog = ({ open, onOpenChange, onSuccess }) => {
  const { currentTeam } = useTeam();
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dashboard_area: 'medical',
    tags: '',
    shared_with_all_support: false,
    shared_with_all_providers: false,
    file: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', formData.file);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('dashboard_area', formData.dashboard_area);
      formDataToSend.append('tags', JSON.stringify(formData.tags.split(',').map(t => t.trim()).filter(t => t)));
      formDataToSend.append('shared_with_member_ids', JSON.stringify([]));
      formDataToSend.append('shared_with_all_support', formData.shared_with_all_support);
      formDataToSend.append('shared_with_all_providers', formData.shared_with_all_providers);

      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/documents/upload`,
        {
          method: 'POST',
          body: formDataToSend,
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Document uploaded successfully');
        onOpenChange(false);
        onSuccess();
        setFormData({
          title: '',
          description: '',
          dashboard_area: 'medical',
          tags: '',
          shared_with_all_support: false,
          shared_with_all_providers: false,
          file: null
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="upload-document-btn">
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload and organize important documents. Max file size: 10MB
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="file">File *</Label>
            <Input
              id="file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.txt"
              required
              onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
              className="mt-1"
              data-testid="file-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Accepted: PDF, JPG, PNG, DOCX, TXT
            </p>
          </div>

          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Insurance Card - Blue Cross"
              required
              className="mt-1"
              data-testid="title-input"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add notes about this document..."
              className="mt-1"
              rows={3}
              data-testid="description-input"
            />
          </div>

          <div>
            <Label htmlFor="category">Category *</Label>
            <select
              id="category"
              value={formData.dashboard_area}
              onChange={(e) => setFormData({ ...formData, dashboard_area: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
              required
              data-testid="category-select"
            >
              <option value="medical">Medical</option>
              <option value="financial">Financial</option>
              <option value="legal">Legal</option>
              <option value="household">Household</option>
              <option value="caregiving">Caregiving</option>
              <option value="end_of_life">End of Life</option>
              <option value="memorial">Memorial</option>
              <option value="gratitude">Gratitude</option>
            </select>
          </div>

          <div>
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="e.g., insurance, 2024, blue cross"
              className="mt-1"
              data-testid="tags-input"
            />
          </div>

          <div className="border-t pt-4">
            <Label className="mb-3 block font-semibold">Sharing Settings</Label>
            <div className="space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.shared_with_all_support}
                  onChange={(e) => setFormData({ ...formData, shared_with_all_support: e.target.checked })}
                  className="mt-1"
                  data-testid="share-support-checkbox"
                />
                <div>
                  <div className="font-medium">Share with all Support members</div>
                  <div className="text-sm text-gray-500">Friends and family</div>
                </div>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.shared_with_all_providers}
                  onChange={(e) => setFormData({ ...formData, shared_with_all_providers: e.target.checked })}
                  className="mt-1"
                  data-testid="share-providers-checkbox"
                />
                <div>
                  <div className="font-medium">Share with all Provider members</div>
                  <div className="text-sm text-gray-500">Healthcare professionals</div>
                </div>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Core members always have access
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploading} className="flex-1" data-testid="submit-upload-btn">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ShareDocumentDialog = ({ document, open, onOpenChange, onSuccess }) => {
  const { currentTeam } = useTeam();
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [shareWithSupport, setShareWithSupport] = useState(false);
  const [shareWithProviders, setShareWithProviders] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && currentTeam) {
      fetchTeamMembers();
      setSelectedMembers(document.shared_with_member_ids || []);
      setShareWithSupport(document.shared_with_all_support || false);
      setShareWithProviders(document.shared_with_all_providers || false);
    }
  }, [open, currentTeam]);

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/members`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const nonCoreMembers = data.filter(m => m.role !== 'core');
        setTeamMembers(nonCoreMembers);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleShare = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/documents/${document.id}/share`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            member_ids: selectedMembers,
            share_with_all_support: shareWithSupport,
            share_with_all_providers: shareWithProviders
          }),
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Sharing settings updated');
        onOpenChange(false);
        onSuccess();
      } else {
        toast.error('Failed to update sharing settings');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Error updating sharing settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Share2 className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Document</DialogTitle>
          <DialogDescription>
            Choose who can access "{document.title}"
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
            <strong>Note:</strong> Core members always have access
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={shareWithSupport}
                onChange={(e) => setShareWithSupport(e.target.checked)}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Share with all Support members</div>
              </div>
            </label>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={shareWithProviders}
                onChange={(e) => setShareWithProviders(e.target.checked)}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Share with all Provider members</div>
              </div>
            </label>
          </div>

          {teamMembers.length > 0 && (
            <div>
              <Label className="mb-2 block">Share with specific members</Label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                {teamMembers.map(member => (
                  <label key={member.user_id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member.user_id)}
                      onChange={() => toggleMember(member.user_id)}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{member.user_name}</div>
                      <div className="text-xs text-gray-500">{member.role}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button onClick={handleShare} disabled={loading} className="flex-1">
              {loading ? 'Updating...' : 'Update Sharing'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Documents;
